/**
 * @file This file contains the assignments store.
 */
import axios from 'axios';
import { EitherAsync, Left, Maybe, Right } from 'purify-ts';
import { getStoreBuilder } from 'vuex-typex';
import { eitherAsync, object } from '@codegrade/utils';
import client from '@/client';
import * as models from '@/models';
import { Assignment } from '@/models';
import { CoursesStore, store } from '@/store';
import * as utils from '@/utils';
import { AutoTestStore } from './autotest';
import { PeerFeedbackStore } from './peer_feedback';
const storeBuilder = getStoreBuilder();
const moduleBuilder = storeBuilder.module('assignments', {
    assignments: {},
    assignmentTimeframes: {},
    assignmentLoaders: {},
    timeframeLoaders: {},
});
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const forceLoadRubric = (assignmentId) => EitherAsync(() => store.dispatch('rubrics/loadRubric', { assignmentId, force: true }, { root: true }));
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var AssignmentsStore;
(function (AssignmentsStore) {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AssignmentsStore.commitAssignment = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { assignment } = payload;
        utils.vueSet(state.assignments, assignment.id, assignment);
    }, 'commitAssignment');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AssignmentsStore.commitAssignments = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { assignmentsById } = payload;
        state.assignments = { ...state.assignments, ...assignmentsById };
    }, 'commitAssignments');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AssignmentsStore.commitClearAll = moduleBuilder.commit(state => {
        state.assignments = {};
        state.assignmentTimeframes = {};
        state.assignmentLoaders = {};
        state.timeframeLoaders = {};
    }, 'commitClearAll');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AssignmentsStore.commitRemoveAssignment = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        utils.vueDelete(state.assignments, payload.assignmentId);
        utils.vueDelete(state.assignmentLoaders, payload.assignmentId);
    }, 'commitRemoveAssignment');
    // eslint-disable-next-line
    function _getAssignmentTimeframe(state) {
        return (assignmentId) => Maybe.fromNullable(state.assignmentTimeframes[assignmentId]);
    }
    // eslint-disable-next-line
    function _getAssignment(state) {
        return (assignmentId) => Maybe.fromNullable(state.assignments?.[assignmentId]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AssignmentsStore.getAssignment = moduleBuilder.read(_getAssignment, 'getAssignment');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AssignmentsStore.allAssignments = moduleBuilder.read(state => Object.values(state.assignments), 'allAssignments');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const removeLoader = moduleBuilder.commit((state, payload) => {
        utils.vueDelete(state.assignmentLoaders, payload.assignmentId);
    }, 'removeLoader');
    const commitLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { assignmentId, value } = payload;
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const removeAfter = () => removeLoader({ assignmentId });
        value.then(removeAfter, removeAfter);
        utils.vueSet(state.assignmentLoaders, assignmentId, value);
    }, 'commitLoader');
    const commitTimeframeLoader = moduleBuilder.commit((state, { assignmentId, maybeLoader, }) => {
        maybeLoader.caseOf({
            Just: loader => utils.vueSet(state.timeframeLoaders, assignmentId, loader),
            Nothing: () => utils.vueDelete(state.timeframeLoaders, assignmentId),
        });
    }, 'commitTimeframeLoader');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AssignmentsStore.updateAssignment = moduleBuilder.dispatch((context, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    payload) => {
        const { assignmentId, assignmentProps } = payload;
        const assig = _getAssignment(context.state)(assignmentId);
        if (assignmentProps.peerFeedbackSettings != null) {
            PeerFeedbackStore.commitClearConnections({ assignmentId });
        }
        assig.caseOf({
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            Just(value) {
                AssignmentsStore.commitAssignment({ assignment: value.update(assignmentProps) });
            },
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            Nothing() {
                throw new ReferenceError('Could not find assignment');
            },
        });
    }, 'updateAssignment');
    const addTimeframe = moduleBuilder.dispatch(({ state }, { assignmentId, data, }) => {
        utils.vueSet(state.assignmentTimeframes, assignmentId, data);
    }, 'addTimeframe');
    /** Get the timeframes connected to an assignment. */
    AssignmentsStore.getTimeframe = moduleBuilder.read(state => 
    /**
     * Get the timeframes connected to an assignment.
     *
     * @param assignmentId The id of the assignment to get the timeframes for.
     * @returns The timeframes connected to the given assignment, if they have already been loaded.
     */
    (assignmentId) => Maybe.fromNullable(state.assignmentTimeframes[assignmentId]), 'getTimeframe');
    /** Load the timeframes connected to an assignment. */
    AssignmentsStore.loadAssignmentTimeframe = moduleBuilder.dispatch(({ state }, { assignmentId, force = false, }) => utils.loadApiWithMaybeForce({
        value: _getAssignmentTimeframe(state)(assignmentId),
        force,
        loader: Maybe.fromNullable(state.timeframeLoaders[assignmentId]),
        load: () => client.assignment.getTimeframes({ assignmentId }).ifRight(({ cgResult }) => {
            addTimeframe({ assignmentId, data: cgResult });
        }),
        addLoader: loader => commitTimeframeLoader({ assignmentId, maybeLoader: Maybe.of(loader) }),
        removeLoader: () => commitTimeframeLoader({ assignmentId, maybeLoader: Maybe.empty() }),
    }), 'loadAssignmentTimeframe');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AssignmentsStore.loadSingleAssignment = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    ({ state }, payload) => {
        const { assignmentId, courseId, force = false } = payload;
        // A quick early quit, without awaiting any promise.
        if (!force && _getAssignment(state)(assignmentId).isJust()) {
            return Promise.resolve();
        }
        if (state.assignmentLoaders[assignmentId] == null || force) {
            commitLoader({
                assignmentId,
                value: (async () => {
                    // We first try to load the assignment by
                    // `courseId` to prevent unnecessary
                    // requests. If one part of the code loads
                    // course 5 (which has assignment 4 in it) and
                    // another part the single assignment 4 we
                    // only want to load course 5 as this will
                    // already result in assignment 4.
                    if (courseId != null) {
                        await CoursesStore.loadSingleCourse({ courseId, force });
                    }
                    if ((force && courseId == null) ||
                        _getAssignment(state)(assignmentId).isNothing()) {
                        await client.assignment
                            .getCourse({ assignmentId })
                            .then(response => response.unsafeCoerce())
                            .then(({ cgResult }) => CoursesStore.addCourse({ course: cgResult }));
                    }
                })(),
            });
        }
        return state.assignmentLoaders[assignmentId];
    }, 'loadSingleAssignment');
    /** Update the timeframes of an assignment. */
    AssignmentsStore.putTimeframes = moduleBuilder.dispatch((_, { assignmentId, timeframes, }) => client.assignment
        .putTimeframes({ assignmentId, requestBody: timeframes })
        .chain(resp => AssignmentsStore.loadSingleAssignment({
        courseId: AssignmentsStore.getAssignment()(assignmentId)
            .map(object.attr('courseId'))
            .extract(),
        assignmentId,
        force: true,
    }).then(() => Right(resp)))
        .map(resp => resp.addCallbacks({
        onAfterSuccess: () => {
            addTimeframe({ assignmentId, data: resp.cgResult });
        },
    })), 'putTimeframe');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AssignmentsStore.patchAssignment = moduleBuilder.dispatch((_, payload) => {
        const { assignmentId, assignmentProps, submitTypes } = payload;
        const initial = submitTypes == null
            ? utils.emptyResponse()
            : client.assignment.patchSubmitTypes({
                assignmentId,
                requestBody: {
                    json: {
                        filesUploadEnabled: submitTypes.files,
                        webhookConfiguration: submitTypes.webhookConfiguration,
                        editorUploadEnabled: submitTypes.editor,
                    },
                    template: submitTypes.template?.map(x => x),
                },
            });
        return initial.chain(templateRes => client.assignment
            .patch({
            assignmentId,
            requestBody: assignmentProps,
        })
            .map(patchRes => patchRes.addWarnings(templateRes).addCallbacks({
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            onAfterSuccess() {
                AssignmentsStore.commitAssignment({
                    assignment: Assignment.fromApiServerData(patchRes.cgResult),
                });
            },
        })));
    }, 'patchAssignment');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AssignmentsStore.createAssignment = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async (_, { courseId, name }) => {
        const response = await client.course
            .createAssignment({
            courseId,
            requestBody: { name },
        })
            .then(res => res.unsafeCoerce());
        const assignment = Assignment.fromApiServerData(response.cgResult);
        return response.addCallbacks({
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            onAfterSuccess() {
                AssignmentsStore.commitAssignment({ assignment });
                CoursesStore.commitAssignment({ courseId, assignment });
            },
        });
    }, 'createAssignment');
    /** The dispatch to do after you have import into an assignment. */
    AssignmentsStore.afterImport = moduleBuilder.dispatch((_, { intoAssignment, }) => eitherAsync.every(forceLoadRubric(intoAssignment.id).chainLeft(err => {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
            return utils.emptyResponse();
        }
        return eitherAsync.leftFromValue(err);
    }), AutoTestStore.loadAutoTestByAssignment({
        assignmentId: intoAssignment.id,
        force: true,
    }).chainLeft(utils.handleApiError({
        404: () => null,
        403: () => null,
    }))), 'afterImport');
    /** Copy the target assignment into the source. */
    AssignmentsStore.copyAssignment = moduleBuilder.dispatch((_, payload) => client.assignment
        .importInto({
        intoAssignmentId: payload.into.id,
        requestBody: {
            fromAssignmentId: payload.from.id,
            allowExistingSubmissions: payload.allowExistingSubmissions === true,
        },
    })
        .chain(response => eitherAsync
        .every(AssignmentsStore.afterImport({ intoAssignment: payload.into }), EitherAsync.fromPromise(() => CoursesStore.loadSingleCourse({
        courseId: payload.into.courseId,
        force: true,
    }).then(Right, Left)))
        .map(() => response))
        .ifRight(response => AssignmentsStore.commitAssignment({
        assignment: models.Assignment.fromApiServerData(response.body),
    })), 'copyAssignment');
    utils.signal.AFTER_USER_LOGGED_OUT.register(() => {
        AssignmentsStore.commitClearAll();
    });
})(AssignmentsStore || (AssignmentsStore = {}));
